<template>
  <!--
    UM BÔNUS DO CLÃ, como o D22 manda (problemas 3 e 5): não "o nível como número solto", mas o
    efeito ATUAL em número, quanto falta no caixa para o próximo nível (o medidor dá meta ao
    caixa), se está ativo e quanto tempo resta. E o motivo de um botão travado vai escrito NELE
    (7.4e): o membro lê "só o líder ativa" em vez de encarar um botão morto.
  -->
  <article class="bonus" :class="{ 'bonus--ativo': ativo }">
    <div class="bonus__cabecalho">
      <FishIcon :nome="icone" tamanho="medio" />
      <div class="crescer">
        <p class="bonus__nome">
          {{ $t(`clan.boosts.${type}`) }}
          <span class="bonus__nivel t-numero">Nv. {{ nivel }}</span>
        </p>
        <p class="bonus__efeito">
          {{ efeitoAtual }}
          <span v-if="ativo" class="bonus__aceso">
            · {{ $t('clan.timeRemaining') }}: {{ tempoRestante }}
          </span>
        </p>
      </div>
    </div>

    <!-- A meta do caixa: quanto do custo do próximo nível já está no baú (D22, problema 5). -->
    <FishMeter
      :valor="Math.min(clan.balance || 0, custoDeUpgrade)"
      :maximo="custoDeUpgrade || 1"
      cor="ouro"
      tamanho="fino"
      :rotulo="`${$t('clan.upgradeBoost')}: ${formato(custoDeUpgrade)}`"
      :rotulo-acessivel="$t(`clan.boosts.${type}`)"
    />

    <div class="bonus__acoes">
      <FishButton
        tamanho="pequeno"
        :desabilitado="!isLeader || !caixaCobreUpgrade"
        :motivo="motivoDoUpgrade"
        som="clique"
        @click="$emit('upgrade', type)"
      >
        {{ $t('clan.upgradeBoost') }} · {{ formato(custoDeUpgrade) }}
      </FishButton>

      <FishButton
        v-if="nivel > 0 && !ativo"
        variante="primario"
        tamanho="pequeno"
        :desabilitado="!isLeader || !caixaCobreAtivacao"
        :motivo="motivoDaAtivacao"
        som="clique"
        @click="$emit('activate', type)"
      >
        {{ $t('clan.activateBoost') }} · {{ formato(custoDeAtivacao) }}
      </FishButton>
    </div>
  </article>
</template>

<script>
import { FishButton, FishIcon, FishMeter } from '@/shared/ui';

const ICONES = {
  moreGold: 'coins',
  moreExp: 'graduation-cap',
  moreFishingPower: 'atributo-poder',
};

export default {
  name: 'ClanBoostCard',
  components: { FishButton, FishIcon, FishMeter },
  props: {
    type: { type: String, required: true },
    clan: { type: Object, required: true },
    isLeader: { type: Boolean, default: false },
  },
  emits: ['upgrade', 'activate'],
  computed: {
    icone() {
      return ICONES[this.type] || 'bolt';
    },
    nivel() {
      const sufixo = this.type.charAt(0).toUpperCase() + this.type.slice(1);
      return this.clan.clan_boost?.[`lvl${sufixo}`] || 0;
    },
    ativo() {
      return !!this.clan.boosts?.[this.type];
    },
    tempoRestante() {
      const ms = this.clan.boosts?.[this.type]?.timeRemaining || 0;
      const horas = Math.floor(ms / 3600000);
      const minutos = Math.floor((ms % 3600000) / 60000);
      return horas > 0 ? `${horas}h ${minutos}m` : `${minutos}m`;
    },
    /**
     * O efeito em NÚMERO, do domínio (`packages/domain/clas.ts`): `moreGold`/`moreExp` dão
     * +nível%, `moreFishingPower` dá +nível/10 de poder. Nível 0 mostra o que o nível 1 daria —
     * é a tela vendendo o próximo passo. `clan.effect.*` são os gabaritos com placeholder que o
     * i18n já tinha.
     */
    efeitoAtual() {
      const n = Math.max(1, this.nivel);
      const texto = this.type === 'moreFishingPower'
        ? this.$t('clan.effect.moreFishingPower', { power: (n / 10).toFixed(1) })
        : this.$t(`clan.effect.${this.type}`, { percent: n });
      return this.nivel === 0 ? `Nv. 1: ${texto}` : texto;
    },
    custoDeUpgrade() {
      return this.clan.boostCosts?.[this.type]?.upgrade ?? 0;
    },
    custoDeAtivacao() {
      return this.clan.boostCosts?.[this.type]?.activation ?? 0;
    },
    caixaCobreUpgrade() {
      return (this.clan.balance || 0) >= this.custoDeUpgrade;
    },
    caixaCobreAtivacao() {
      return (this.clan.balance || 0) >= this.custoDeAtivacao;
    },
    /** 7.4e: o porquê no botão. Só o líder mexe (regra do servidor, certa para os dois tipos). */
    motivoDoUpgrade() {
      if (!this.isLeader) return this.$t('clan.soLider');
      if (!this.caixaCobreUpgrade) return this.$t('clan.caixaInsuficiente');
      return '';
    },
    motivoDaAtivacao() {
      if (!this.isLeader) return this.$t('clan.soLider');
      if (!this.caixaCobreAtivacao) return this.$t('clan.caixaInsuficiente');
      return '';
    },
  },
  methods: {
    formato(n) {
      return new Intl.NumberFormat('pt-BR').format(n || 0);
    },
  },
};
</script>

<style scoped>
.bonus {
  border: var(--moldura-borda) solid transparent;
  border-image: url('/ui/frame-panel.png') var(--moldura-fatia) / var(--moldura-borda) repeat;
  background: var(--papel);
  background-clip: padding-box;
  image-rendering: pixelated;
  padding: var(--e-3);
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
  color: var(--tinta);
}

/* O bônus aceso: o fio de latão embaixo — a mesma língua do fio de raridade (D0, sem pulso). */
.bonus--ativo {
  border-bottom: 5px solid var(--latao);
}

.bonus__cabecalho {
  display: flex;
  align-items: center;
  gap: var(--e-2);
}

.bonus__nome {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  display: flex;
  align-items: baseline;
  gap: var(--e-2);
}

.bonus__nivel {
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.bonus__efeito {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.bonus__aceso {
  color: var(--latao);
}

.bonus__acoes {
  display: flex;
  gap: var(--e-2);
  flex-wrap: wrap;
  /* O motivo dos botões pende abaixo deles. */
  padding-bottom: 1.25em;
}
</style>
